import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { AddressSearch } from './AddressSearch';
import { LandMap, MapFullscreen, resolveOfficialCadastreProvider } from './LandMap';
import { MarketPrice } from './MarketPrice';
import { QuickTest } from './QuickTest';
import { LandIntelligence } from '../LandIntelligence';
import { resolveMarketLocation } from '@/utils/marketData';
import { formatMoney } from '@/utils/formatters';
import { resolveCadastreAvailability } from '@/utils/cadastreAvailability';
import { resolveStreetBand } from '@/utils/marketBands';

export function LandTab({
  visible, inputs, onInputChange, cadastreData, onCadastreData, marketData, onMarketData, assumptions, decision, fx = null,
  readOnly = false,
}) {
  // ── Address state ──
  const [address, setAddress] = useState(inputs.address || '');
  const [mapAddress, setMapAddress] = useState(inputs.address || '');
  const [fullscreen, setFullscreen] = useState(false);
  const [mapMode, setMapMode] = useState('satellite');
  const [locationData, setLocationData] = useState(inputs.locationData || {
    postcode: '', city: '', lat: null, lon: null, selectedAddress: '',
  });
  const [landRefreshKey, setLandRefreshKey] = useState(0);
  const timerRef = useRef(null);
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;
  const priorLocForMarketRef = useRef(null);
  const searchDirtyRef = useRef(false);

  useEffect(() => {
    if (mapMode === 'earth') {
      setMapMode('satellite');
      return;
    }
    if (mapMode === 'cadastre' && !resolveOfficialCadastreProvider(cadastreData, mapAddress)) {
      setMapMode('satellite');
    }
  }, [cadastreData, mapMode, mapAddress]);

  // Drop stale market when postcode or commune updates (e.g. cadastre adds city after postcode-only fetch).
  useEffect(() => {
    const p = locationData?.postcode || '';
    const c = locationData?.city || '';
    if (priorLocForMarketRef.current === null) {
      priorLocForMarketRef.current = { p, c };
      return;
    }
    const prev = priorLocForMarketRef.current;
    if (p === prev.p && c === prev.c) return;
    const cityBecameAvailable = Boolean(c && !prev.c);
    const postcodeChanged = p !== prev.p;
    if (cityBecameAvailable || postcodeChanged) {
      onMarketData(null);
    }
    priorLocForMarketRef.current = { p, c };
  }, [locationData?.postcode, locationData?.city, onMarketData]);

  // Sync address when inputs change externally (deal load)
  const prevAddressRef = useRef(inputs.address);
  useEffect(() => {
    if (inputs.address !== prevAddressRef.current) {
      setAddress(inputs.address || '');
      setMapAddress(inputs.address || '');
      searchDirtyRef.current = false;
      prevAddressRef.current = inputs.address;
    }
    if (inputs.locationData) {
      setLocationData({
        postcode: '', city: '', lat: null, lon: null, selectedAddress: '', ...inputs.locationData,
      });
    }
  }, [inputs.address, inputs.locationData]);

  // Extract postcode from address — skip while user has diverged from a resolved parcel (QA-018)
  useEffect(() => {
    if (searchDirtyRef.current) return;
    if (!locationData.postcode && address.length >= 4) {
      const m = address.match(/\b([1-9]\d{3})\b/);
      if (m) setLocationData(prev => ({ ...prev, postcode: m[1] }));
    }
  }, [address]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist locationData to inputs
  useEffect(() => {
    if ((locationData.postcode || locationData.city || locationData.lat !== null || locationData.lon !== null) &&
        (locationData.postcode !== (inputsRef.current.locationData?.postcode || '') ||
         locationData.city !== (inputsRef.current.locationData?.city || '') ||
         (locationData.lat ?? null) !== (inputsRef.current.locationData?.lat ?? null) ||
         (locationData.lon ?? null) !== (inputsRef.current.locationData?.lon ?? null) ||
         (locationData.selectedAddress || '') !== (inputsRef.current.locationData?.selectedAddress || ''))) {
      onInputChange({ ...inputsRef.current, locationData });
    }
  }, [locationData.postcode, locationData.city, locationData.lat, locationData.lon, locationData.selectedAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddressChange = useCallback((val) => {
    setAddress(val);
    const resolved = (locationData.selectedAddress || '').trim();
    const next = (val || '').trim();
    // QA-018: typing away from a resolved parcel must drop stale land/market assumptions
    const divergedFromResolved = Boolean(resolved && next !== resolved);
    const cleared = !next;
    clearTimeout(timerRef.current);
    if (divergedFromResolved || cleared) {
      searchDirtyRef.current = true;
      onCadastreData(null);
      onMarketData(null);
      const emptyLoc = { postcode: '', city: '', lat: null, lon: null, selectedAddress: '' };
      setLocationData(emptyLoc);
      // Update mapAddress immediately so LandIntelligence does not refetch the old parcel.
      // Do NOT bump landRefreshKey here — that would force a MarketPrice API call per keystroke.
      setMapAddress(cleared ? '' : val);
      onInputChange({ ...inputsRef.current, address: val, locationData: emptyLoc });
    } else {
      onInputChange({ ...inputsRef.current, address: val });
      timerRef.current = setTimeout(() => setMapAddress(val), 800);
    }
  }, [onInputChange, onCadastreData, onMarketData, locationData.selectedAddress]);

  const handleAddressSelect = useCallback((selected, data) => {
    searchDirtyRef.current = false;
    setAddress(selected);
    setMapAddress(selected);
    onMarketData(null);
    onCadastreData(null);
    const update = { ...inputsRef.current, address: selected };
    if (data) { setLocationData(data); update.locationData = data; }
    onInputChange(update);
    setLandRefreshKey((k) => k + 1);
  }, [onInputChange, onMarketData, onCadastreData]);

  const handleClearAddress = useCallback(() => {
    searchDirtyRef.current = false;
    clearTimeout(timerRef.current);
    setAddress('');
    setMapAddress('');
    setLocationData({ postcode: '', city: '', lat: null, lon: null, selectedAddress: '' });
    onMarketData(null);
    onCadastreData(null);
    onInputChange({
      ...inputsRef.current,
      address: '',
      locationData: { postcode: '', city: '', lat: null, lon: null, selectedAddress: '' },
    });
    setLandRefreshKey((k) => k + 1);
  }, [onInputChange, onMarketData, onCadastreData]);

  const handleCadastreCommune = useCallback((commune, pc) => {
    // Ignore commune updates while the user is mid-edit away from a resolved parcel
    if (searchDirtyRef.current) return;
    setLocationData(prev => {
      const next = {
        ...prev,
        postcode: pc || prev.postcode || '',
        city: commune || prev.city || '',
      };
      if (next.postcode === prev.postcode && next.city === prev.city) {
        return prev;
      }
      return next;
    });
  }, []);

  // ── Refresh ──
  const { postcode: resolvedPostcode, city: resolvedCity } = resolveMarketLocation({
    locationData,
    cadastreData,
    address: mapAddress,
  });

  const handleRefreshLand = useCallback(() => {
    const pcMatch = mapAddress?.match(/\b([1-9]\d{3})\b/);
    const resetLocationData = {
      postcode: pcMatch ? pcMatch[1] : '',
      city: '',
      lat: null,
      lon: null,
      selectedAddress: '',
    };
    setLocationData(resetLocationData);
    onMarketData(null);
    onCadastreData(null);
    onInputChange({
      ...inputsRef.current,
      locationData: resetLocationData,
      targetLandPrice: 0,
      targetPricePerSqm: 0,
    });
    setLandRefreshKey(k => k + 1);
  }, [mapAddress, onMarketData, onCadastreData, onInputChange]);

  if (!visible) return null;

  const countryCode = (assumptions?.countryCode || 'CH').toUpperCase();
  const opCur = (assumptions?.operatingCurrency || 'CHF').toUpperCase();
  const cadastre = resolveCadastreAvailability(countryCode);

  if (readOnly) {
    const addr = mapAddress || address || inputs.address || '—';
    return (
      <div className="lg:flex-1 lg:overflow-y-auto lg:min-h-0">
        <div className="p-3 space-y-3" data-testid="land-tab" data-readonly="true">
          <div className="text-[8px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-2 py-1">
            Land context — display only
          </div>
          <div className="text-[11px] text-slate-800 font-medium break-words" data-testid="share-address-display">
            {addr}
          </div>
          <div
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[9px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1"
          >
            <span className="font-bold text-slate-500 uppercase tracking-wide text-[7px]">In deal</span>
            <span className="tabular-nums">
              {(Number(inputs.landSize) || 0).toLocaleString('de-CH')} m²
            </span>
            <span className="text-slate-300">·</span>
            <span className="tabular-nums">{formatMoney(inputs.landPrice || 0, opCur)}</span>
          </div>
          <LandMap
            address={mapAddress || addr}
            cadastreData={cadastreData}
            mode={mapMode}
            onModeChange={setMapMode}
            onFullscreen={() => setFullscreen(true)}
          />
          {fullscreen && (
            <MapFullscreen
              address={mapAddress || addr}
              cadastreData={cadastreData}
              mode={mapMode}
              onModeChange={setMapMode}
              onClose={() => setFullscreen(false)}
            />
          )}
          {!cadastre.enabled && (
            <div className="rounded border border-slate-200 bg-slate-50 px-2.5 py-2 text-[9px] text-slate-600">
              <div className="font-semibold text-slate-700 mb-0.5">{cadastre.title}</div>
              <p className="leading-snug">{cadastre.body}</p>
            </div>
          )}
          {marketData?.status === 'ok' && (() => {
            const band = resolveStreetBand(marketData);
            const quote = marketData.quote_currency || opCur;
            const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('de-CH'));
            return (
              <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-2 text-[9px] text-indigo-800" data-testid="share-market-range">
                <div className="font-bold text-indigo-600 uppercase text-[8px] mb-1">Market reference (snapshot)</div>
                <div className="tabular-nums">
                  Street mid:{' '}
                  <span className="font-semibold">{fmt(band.mid ?? marketData.street_average ?? marketData.price_per_sqm)}</span>
                  {' '}{quote}/m²
                </div>
                {(band.min != null || band.max != null) && (
                  <div className="tabular-nums mt-0.5" data-testid="share-market-range-values">
                    Range:{' '}
                    <span className="font-semibold">{fmt(band.min)} – {fmt(band.max)}</span>
                    {' '}{quote}/m²
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  return (
    <div className="lg:flex-1 lg:overflow-y-auto lg:min-h-0">
      <div className="p-3 space-y-3" data-testid="land-tab">
        {/* Address — dominant search */}
        <AddressSearch
          value={address}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
          onClear={handleClearAddress}
          countryCode={countryCode}
        />

        {/* Deal land inputs live on the left — this panel reflects / can update them */}
        <div
          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[9px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1"
          data-testid="deal-land-inputs-strip"
        >
          <span className="font-bold text-slate-500 uppercase tracking-wide text-[7px]">In deal</span>
          <span className="tabular-nums">
            {(Number(inputs.landSize) || 0).toLocaleString('de-CH')} m²
          </span>
          <span className="text-slate-300">·</span>
          <span className="tabular-nums">{formatMoney(inputs.landPrice || 0, opCur)}</span>
          <span className="text-[7px] text-slate-400 ml-auto">Edit left · cadastre surface applies here</span>
        </div>

        {/* Map — larger, primary */}
        <LandMap
          address={mapAddress}
          cadastreData={cadastreData}
          mode={mapMode}
          onModeChange={setMapMode}
          onFullscreen={() => setFullscreen(true)}
        />
        {fullscreen && (
          <MapFullscreen
            address={mapAddress}
            cadastreData={cadastreData}
            mode={mapMode}
            onModeChange={setMapMode}
            onClose={() => setFullscreen(false)}
          />
        )}

        {/* Land Intelligence (cadastre) — Swiss only; other countries get honest portal links */}
        {cadastre.enabled ? (
          <LandIntelligence
            address={mapAddress}
            locationData={locationData}
            cachedData={cadastreData}
            refreshKey={landRefreshKey}
            onSurfaceAvailable={(surface) => onInputChange({ ...inputs, landSize: surface })}
            onCadastreCommune={handleCadastreCommune}
            onDataLoaded={onCadastreData}
          />
        ) : (
          <div
            className="rounded border border-slate-200 bg-slate-50 px-2.5 py-2 text-[9px] text-slate-600"
            data-testid="cadastre-unavailable"
          >
            <div className="font-semibold text-slate-700 mb-0.5">{cadastre.title}</div>
            <p className="leading-snug mb-1.5">{cadastre.body}</p>
            {cadastre.linkUrl && (
              <a
                href={cadastre.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex text-[9px] font-semibold text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                data-testid="cadastre-external-link"
              >
                {cadastre.linkLabel || 'Open official portal'}
              </a>
            )}
          </div>
        )}

        {/* Market Price */}
        <MarketPrice
          address={mapAddress}
          addressLabel={mapAddress}
          postcode={resolvedPostcode}
          city={resolvedCity}
          requestedLocationLabel={[resolvedPostcode, resolvedCity || mapAddress].filter(Boolean).join(' · ')}
          currentPrice={inputs.pricePerSqm}
          onApplyAssumptions={(patch) => onInputChange((prev) => ({ ...prev, ...patch }))}
          cachedResult={marketData}
          onResultLoaded={onMarketData}
          refreshKey={landRefreshKey}
          operatingCurrency={opCur}
          countryCode={countryCode}
          fx={fx}
        />

        {/* Quick Test */}
        <QuickTest inputs={inputs} onInputChange={onInputChange} cadastreData={cadastreData} marketData={marketData} assumptions={assumptions} roiTargets={decision.warnings?.filter(w => w.type === 'target') || []} refreshKey={landRefreshKey} />

        {/* Refresh Land Data CTA */}
        {mapAddress && mapAddress.length >= 5 && (
          <button
            onClick={handleRefreshLand}
            className="w-full flex items-center justify-center gap-1.5 text-[11px] md:text-[9px] font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg py-3 md:py-2 transition-colors"
            data-testid="refresh-land-btn"
          >
            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" />
            Refresh Land Data
          </button>
        )}
      </div>
    </div>
  );
}
